import { Component, useEffect, useState, type ReactNode } from 'react';

import {
  isAuthenticated,
  myOrganisation,
  signOut,
  twoStepEnabled,
  type Organisation,
  type SessionUser,
} from './api.ts';
import { AgencyApp } from './agency/AgencyApp.tsx';
import {
  acceptInvitation,
  captureInviteFromUrl,
  clearPendingInvite,
  pendingInvite,
} from './invite/invite-api.ts';
import { InviteLanding } from './invite/InviteLanding.tsx';
import { Login } from './Login.tsx';
import { CheckEmail } from './signup/CheckEmail.tsx';
import {
  accountState,
  captureVerifyFromUrl,
  takeVerifyToken,
  verifyEmail,
} from './signup/signup-api.ts';
import { SignUp } from './signup/SignUp.tsx';
import { TwoStepSetup } from './login/TwoStepSetup.tsx';
import { redeemGoogleGrant, takeGoogleGrant } from './login/google-signin.ts';
import { PortalApp } from './portal/PortalApp.tsx';
import { PortalBrandContext, PortalNotFound } from './portal/PortalBrand.tsx';
import { usePortalState } from './portal/portal-host.ts';

const PORTAL_READ = 'reports.portal.read';
const STAFF_REPORT_READ = 'reports.document.read';

/** A client login: it can see a portal and none of the agency's own screens. */
function isPortalOnly(organisation: Organisation): boolean {
  return (
    organisation.permissions.includes(PORTAL_READ) &&
    !organisation.permissions.includes(STAFF_REPORT_READ)
  );
}

// An invitation link is read once, before anything renders, and its token taken out of the address bar.
captureInviteFromUrl();
captureVerifyFromUrl();

class Boundary extends Component<{ children: ReactNode }, { error: string | null }> {
  override state = { error: null as string | null };
  static getDerivedStateFromError(error: unknown) {
    return { error: error instanceof Error ? error.message : String(error) };
  }
  override render() {
    if (this.state.error) {
      return (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 24 }}>
          <div className="card" style={{ maxWidth: 480 }}>
            <strong>Something broke rendering this view.</strong>
            <p className="muted" style={{ marginTop: 8, fontSize: 13.5 }}>
              {this.state.error}
            </p>
            <button
              className="btn small"
              style={{ marginTop: 12 }}
              onClick={() => location.reload()}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [booting, setBooting] = useState(isAuthenticated());
  // Two-step verification is mandatory: a signed-in person without it sees only the setup.
  const [needsTwoStep, setNeedsTwoStep] = useState(false);
  const [invite, setInvite] = useState(pendingInvite());
  const [signIn, setSignIn] = useState<{ email: string; notice: string | null } | null>(null);
  // Self-serve sign-up: the form, then "check your email" until the address is verified.
  const [signingUp, setSigningUp] = useState(false);
  const [checkEmail, setCheckEmail] = useState<{ email: string; signedIn: boolean } | null>(null);
  // C7 — an agency's portal host wears the agency's brand; Google sign-in may need the code step.
  const portal = usePortalState();
  const [googleChallenge, setGoogleChallenge] = useState('');

  const enter = async (signedIn: SessionUser) => {
    // The address first, then two-step: the API refuses everything else until both are done.
    if (!(await accountState()).email_verified) {
      setUser(signedIn);
      setCheckEmail({ email: signedIn.email, signedIn: true });
      return;
    }
    setCheckEmail(null);
    if (!(await twoStepEnabled())) {
      setUser(signedIn);
      setNeedsTwoStep(true);
      return;
    }
    setNeedsTwoStep(false);
    // Joining by invitation: redeemed only now, once signed in with two-step on.
    const token = pendingInvite();
    if (token) {
      clearPendingInvite();
      setInvite(null);
      try {
        await acceptInvitation(token);
      } catch (cause) {
        setSignIn({
          email: '',
          notice: cause instanceof Error ? cause.message : 'The invitation could not be accepted.',
        });
      }
    }
    setOrganisation(await myOrganisation());
    // A spent reset link must not stay in the address bar (or in history): signed out
    // later, the page would reopen the reset screen with a token that no longer works.
    if (new URLSearchParams(window.location.search).has('token')) {
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
    setUser(signedIn);
  };

  // Start-up, in order: a verification link opened in this tab is redeemed
  // FIRST, so a signed-in person is not told to verify an address they just
  // verified; then an existing session carries on.
  useEffect(() => {
    const start = async () => {
      const token = takeVerifyToken();
      if (token) {
        try {
          await verifyEmail(token);
          setSignIn({ email: '', notice: 'Email verified. Sign in to continue.' });
        } catch {
          setSignIn({
            email: '',
            notice:
              'That verification link has expired or was already used. Sign in and we will send a new one.',
          });
        }
      }
      const grant = takeGoogleGrant();
      if (grant) {
        try {
          const result = await redeemGoogleGrant(grant);
          if ('challengeToken' in result) setGoogleChallenge(result.challengeToken);
          else await enter(result.user);
          return;
        } catch (cause) {
          setSignIn({
            email: '',
            notice: cause instanceof Error ? cause.message : 'Google sign-in failed.',
          });
        }
      }
      if (isAuthenticated()) await enter({ id: 'session', email: '' }).catch(() => signOut());
    };
    void start().finally(() => setBooting(false));
  }, []);

  const onSignedIn = (signedIn: SessionUser) => enter(signedIn);

  if (portal.kind === 'unknown') return <PortalNotFound host={portal.host} />;
  if (booting || portal.kind === 'loading') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }} className="muted">
        Loading SynAnalytics…
      </div>
    );
  }
  if (checkEmail) {
    return (
      <CheckEmail
        email={checkEmail.email}
        signedIn={checkEmail.signedIn}
        onContinue={() => (user ? void enter(user) : setCheckEmail(null))}
        onSignOut={() => {
          signOut();
          window.location.reload();
        }}
      />
    );
  }
  if (!user && signingUp) {
    return (
      <SignUp
        onDone={(email) => {
          setSigningUp(false);
          setCheckEmail({ email, signedIn: false });
        }}
        onSignIn={() => setSigningUp(false)}
      />
    );
  }
  if (user && needsTwoStep) return <TwoStepSetup onDone={() => void enter(user)} />;
  if (!user && invite && !signIn) {
    return (
      <InviteLanding
        token={invite}
        onSignIn={(email, notice) => setSignIn({ email, notice: notice ?? null })}
      />
    );
  }
  if (!user || !organisation) {
    return (
      <PortalBrandContext.Provider value={portal.kind === 'portal' ? portal.brand : null}>
        <Login
          key={`${signIn?.email ?? ''}${googleChallenge}`}
          onSignedIn={onSignedIn}
          initialEmail={signIn?.email ?? ''}
          initialNotice={signIn?.notice ?? null}
          initialChallenge={googleChallenge}
          onStartTrial={portal.kind === 'portal' ? undefined : () => setSigningUp(true)}
        />
      </PortalBrandContext.Provider>
    );
  }
  if (isPortalOnly(organisation)) {
    return (
      <Boundary>
        <PortalApp organisation={organisation} />
      </Boundary>
    );
  }
  return (
    <Boundary>
      <AgencyApp organisation={organisation} />
    </Boundary>
  );
}
